import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Helmet } from 'react-helmet';
import { withStyles, useTheme } from '@material-ui/core/styles';
import {
  Button,
  ButtonGroup,
  CircularProgress,
  Typography,
  Snackbar,
  FormControl,
  TextField,
  MenuItem,
  Tooltip,
  Fab,
  Zoom,
  Menu,
} from '@material-ui/core';
import GridIcon from '@material-ui/icons/ViewComfy';
import ListIcon from '@material-ui/icons/Reorder';
import UpArrow from '@material-ui/icons/ArrowUpward';
import Add from '@material-ui/icons/Add';

import MySnackbarContentWrapper from '../../shared/snackbars/MySnackbarContentWrapper';
import NavBar from '../nav/NavBar';
import Footer from '../footer/Footer';
import FooterSpacer from '../footer/FooterSpacer';
import BlogPostGrid from './BlogPostGrid';
import BlogPostList from './BlogPostList';
import BlogSubscribeModal from './BlogSubscribeModal';

import BlogHero from '../../static/img/blog_page/blogHero.png';

import {
  getBlogPosts,
  getAllBlogCategories,
  getAllBlogTags,
  getAllBlogAuthors,
} from '../../shared/requests/BlogRequests';
import { tagShape, authorShape, categoryShape } from './propShapes';

const styles = theme => ({
  appBarSpacer: theme.mixins.toolbar,
  page: {
    position: 'relative',
    width: '100%',
    minHeight: '100vh',
    backgroundColor: '#fafafa',
    overflowX: 'hidden',
  },
  container: {
    width: 'auto',
    maxWidth: '800px',
    marginRight: '210px',
    marginLeft: 'auto',
    [theme.breakpoints.up(1200 + theme.spacing(2 * 2))]: {
      width: 800,
      marginLeft: 'auto',
      marginRight: 'auto',
    },
    [theme.breakpoints.down(850)]: {
      marginRight: theme.spacing(2),
    },
    [theme.breakpoints.down(1050)]: {
      marginLeft: theme.spacing(2),
    },
  },
  hero: {
    height: 'auto',
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    flexDirection: 'column',
    marginTop: theme.spacing(4),
    borderRadius: '4px',
  },
  header: {
    textAlign: 'center',
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(1),
    textTransform: 'uppercase',
    fontWeight: 'bold',
  },
  subHeader: {
    textAlign: 'center',
    marginBottom: theme.spacing(1),
  },
  center: {
    marginTop: theme.spacing(5),
    textAlign: 'center',
  },
  paper: {
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(3),
    padding: theme.spacing(2),
    [theme.breakpoints.up(800 + theme.spacing(3 * 2))]: {
      marginTop: theme.spacing(6),
      marginBottom: theme.spacing(6),
      padding: theme.spacing(3),
    },
  },
  footerSpacer: {
    marginTop: theme.spacing(4),
  },
  renderButtons: {
    paddingTop: '10px',
    position: 'sticky',
    top: '200px',
    [theme.breakpoints.down(1200)]: {
      display: 'none',
    },
  },
  offsetLeft: {
    position: 'absolute',
    marginLeft: '-150px',
    display: 'flex',
    flexDirection: 'column',
  },
  deselectedButton: {
    color: 'Lightgray',
  },
  sortControl: {
    paddingTop: '20px',
    width: '150px',
    marginRight: '-190px',
    position: 'absolute',
    display: 'flex',
    flexDirection: 'column',
  },
  navSortControl: {
    paddingLeft: theme.spacing(1),
    paddingRight: theme.spacing(1),
    width: '150px',
    display: 'flex',
    flexDirection: 'column',
  },
  sortControlBox: {
    display: 'flex',
    justifyContent: 'flex-end',
    [theme.breakpoints.down(850)]: {
      display: 'none',
    },
  },
  upArrow: {
    position: 'fixed',
    right: '2vw',
    top: '50%',
    bottom: '50%',
    zIndex: 1,
  },
  filterBy: {
    marginTop: theme.spacing(4),
    fontWeight: 'bold',
  },
  sortBy: {
    fontWeight: 'bold',
  },
  subscribeButton: {
    marginTop: theme.spacing(3),
    [theme.breakpoints.down(1200)]: {
      display: 'none',
    },
  },
  sortControlSubscribeButton: {
    marginTop: theme.spacing(1),
    [theme.breakpoints.up(1200)]: {
      display: 'none',
    },
  },
  clearButton: {
    marginTop: theme.spacing(3),
  },
  clearButtonPulse: {
    marginTop: theme.spacing(3),
    boxShadow: '0 0 0 0 rgba(0, 0, 0, 1)',
    transform: 'scale(1)',
    animation: '$pulse 2s infinite',
  },
  '@keyframes pulse': {
    '0%': {
      transform: 'scale(0.95)',
      boxShadow: '0 0 0 0 rgba(0, 0, 0, 0.7)',
    },
    '70%': {
      transform: 'scale(1)',
      boxShadow: '0 0 0 10px rgba(0, 0, 0, 0)',
    },
    '100%': {
      transform: 'scale(0.95)',
      boxShadow: '0 0 0 0 rgba(0, 0, 0, 0)',
    },

  },
  errorMessage: {
    marginTop: theme.spacing(6),
    textAlign: 'center',
  },
  select: {
    marginTop: theme.spacing(0.6),
  },
  renderContainer: {
    minHeight: '400px',
    marginBottom: theme.spacing(5),
  },
  loadMoreButton: {
    textAlign: 'center',
    marginBottom: theme.spacing(3),
  },
  mobileNav: {
    [theme.breakpoints.up(850)]: {
      display: 'none',
    },
    mobileMenu: {
      padding: '0px',
      width: '240px',
    },
  },
  menuNav: {
    position: 'fixed',
    right: '2vw',
    top: '50%',
    bottom: '50%',
    zIndex: 1,
    [theme.breakpoints.up(850)]: {
      display: 'none',
    },
  },
  searchWrapper: {
    display: 'flex',
    justifyContent: 'center',
  },
  textField: {
    margin: theme.spacing(),
  },
  searchButton: {
    margin: theme.spacing(),
  },
});

class Blog extends React.Component {
  constructor(props) {
    super(props);
    this.initialListStyle = window.innerWidth < 1150 ? 'grid' : 'list';

    this.state = {
      posts: [],
      authors: [],
      categories: [],
      tags: [],
      activeSort: 'newest',
      activeAuthor: '',
      activeCategory: '',
      activeTag: '',
      render: 'loading',
      postDisplay: this.initialListStyle,
      currentPage: 0,
      morePagesAvailable: true,
      anchorEl: null,
      showSubscriptionModal: false,
      snackbarOpen: false,
      snackbarVariant: '',
      snackbarMessage: '',
      query: '',
    };

    this.pageSize = 9;

    this.handleClose = this.handleClose.bind(this);
    this.handleViewChange = this.handleViewChange.bind(this);
    this.handleDateSelect = this.handleDateSelect.bind(this);
    this.handleAuthorSelect = this.handleAuthorSelect.bind(this);
    this.handleCategorySelect = this.handleCategorySelect.bind(this);
    this.handleTagSelect = this.handleTagSelect.bind(this);
    this.handleClear = this.handleClear.bind(this);
    this.handleLoadMore = this.handleLoadMore.bind(this);
    this.handleMenuClose = this.handleMenuClose.bind(this);
    this.handleMenuOpen = this.handleMenuOpen.bind(this);
    this.setRenderByViewPort = this.setRenderByViewPort.bind(this);
    this.handleToggleModal = this.handleToggleModal.bind(this);
    this.handleSearch = this.handleSearch.bind(this);
    this.handleSearchChange = this.handleSearchChange.bind(this);
    this.handleKeyPress = this.handleKeyPress.bind(this);

    this.errorState = {
      snackbarOpen: true,
      snackbarVariant: 'error',
    };
  }

  componentDidMount() {
    window.addEventListener('resize', this.setRenderByViewPort);
    getBlogPosts({ pageSize: this.pageSize })
      .then((response) => {
        this.checkForEmptyResponseAndSetState(response.data);
      })
      .catch(() => {
        this.setState({
          ...this.errorState,
          snackbarMessage: 'Failed to fetch blog posts.',
          render: 'error',
        });
      });
    getAllBlogAuthors()
      .then((response) => {
        this.setState({ authors: response.data });
      })
      .catch(() => {
        this.setState({
          ...this.errorState,
          snackbarMessage: 'Failed to load authors. Author filter disabled',
        });
      });
    getAllBlogTags()
      .then((response) => {
        this.setState({ tags: response.data });
      })
      .catch(() => {
        this.setState({
          ...this.errorState,
          snackbarMessage: 'Failed to load tags. Tag filter disabled',
        });
      });
    getAllBlogCategories()
      .then((response) => {
        this.setState({ categories: response.data });
      })
      .catch(() => {
        this.setState({
          ...this.errorState,
          snackbarMessage: 'Failed to load categories. Category filter disabled',
        });
      });
  }

  setRenderByViewPort() {
    const { postDisplay } = this.state;
    if (window.innerWidth < 1200 && postDisplay !== 'grid') {
      this.setState({ postDisplay: 'grid' });
    }
  }

  handleViewChange(view) {
    this.setState({ postDisplay: view });
  }

  handleSearch() {
    const {
      activeCategory,
      activeTag,
      activeAuthor,
      activeSort,
      query,
    } = this.state;
    this.setState({ render: 'loading', currentPage: 0 });
    getBlogPosts({
      query,
      category: activeCategory,
      tag: activeTag,
      author: activeAuthor,
      sort: activeSort,
      pageSize: this.pageSize,

    })
      .then((response) => {
        this.checkForEmptyResponseAndSetState(response.data);
      })
      .catch((error) => {
        if (error.response.status === 404) {
          this.setState({ render: 'expandSearch' });
        } else {
          this.setState({
            ...this.errorState,
            snackbarMessage: 'Failed to fetch blog posts.',
            render: 'error',
          });
        }
      });
  }

  handleSearchChange(e) {
    this.setState({ query: e.target.value });
  }

  // Allows using enter key to trigger search
  handleKeyPress(e) {
    if (e.keyCode === 13) {
      this.handleSearch();
    }
  }

  handleDateSelect(e) {
    const {
      query,
      activeCategory,
      activeTag,
      activeAuthor,
    } = this.state;

    this.setState({ render: 'loading', activeSort: e.target.value, currentPage: 0 });

    getBlogPosts({
      query,
      author: activeAuthor.id,
      category: activeCategory,
      tag: activeTag,
      sort: e.target.value,
      poge: 0,
      pageSize: this.pageSize,
    })
      .then((response) => {
        this.checkForEmptyResponseAndSetState(response.data);
      }).catch((error) => {
        if (error.response.status === 404) {
          this.setState({ render: 'expandSearch' });
        } else {
          this.setState({
            ...this.errorState,
            snackbarMessage: 'Failed to fetch blog posts.',
            render: 'error',
          });
        }
      });
  }

  handleAuthorSelect(e) {
    const {
      query,
      activeCategory,
      activeTag,
      activeSort,
    } = this.state;
    const { id: authorId } = e.target.value;

    this.setState({ render: 'loading', activeAuthor: e.target.value, currentPage: 0 });

    getBlogPosts({
      query,
      author: authorId,
      category: activeCategory,
      tag: activeTag,
      sort: activeSort,
      page: 0,
      pageSize: this.pageSize,
    })
      .then((response) => {
        this.checkForEmptyResponseAndSetState(response.data);
      }).catch((error) => {
        if (error.response.status === 404) {
          this.setState({ render: 'expandSearch' });
        } else {
          this.setState({
            ...this.errorState,
            snackbarMessage: 'Failed to fetch blog posts.',
            render: 'error',
          });
        }
      });
  }

  handleCategorySelect(e) {
    const {
      query,
      activeAuthor,
      activeTag,
      activeSort,
    } = this.state;

    this.setState({ render: 'loading', activeCategory: e.target.value, currentPage: 0 });

    getBlogPosts({
      query,
      author: activeAuthor.id,
      category: e.target.value,
      tag: activeTag,
      sort: activeSort,
      page: 0,
      pageSize: this.pageSize,
    })
      .then((response) => {
        this.checkForEmptyResponseAndSetState(response.data);
      }).catch((error) => {
        if (error.response.status === 404) {
          this.setState({ render: 'expandSearch' });
        } else {
          this.setState({
            ...this.errorState,
            snackbarMessage: 'Failed to fetch blog posts.',
            render: 'error',
          });
        }
      });
  }

  handleTagSelect(e) {
    const {
      query,
      activeAuthor,
      activeCategory,
      activeSort,
    } = this.state;

    this.setState({ render: 'loading', activeTag: e.target.value, currentPage: 0 });
    getBlogPosts({
      query,
      author: activeAuthor.id,
      category: activeCategory,
      tag: e.target.value,
      sort: activeSort,
      page: 0,
      pageSize: this.pageSize,
    })
      .then((response) => {
        this.checkForEmptyResponseAndSetState(response.data);
      }).catch((error) => {
        if (error.response.status === 404) {
          this.setState({ render: 'expandSearch' });
        } else {
          this.setState({
            ...this.errorState,
            snackbarMessage: 'Failed to fetch blog posts.',
            render: 'error',
          });
        }
      });
  }

  handleClear() {
    this.setState({
      render: 'loading',
      query: '',
      activeSort: 'newest',
      activeCategory: '',
      activeAuthor: '',
      activeTag: '',
      currentPage: 0,
    });
    getBlogPosts({ pageSize: this.pageSize, page: 0 })
      .then((response) => {
        this.checkForEmptyResponseAndSetState(response.data);
        this.setState({ currentPage: 0 });
      })
      .catch((error) => {
        if (error.response.status === 404) {
          this.setState({ render: 'expandSearch' });
        } else {
          this.setState({
            ...this.errorState,
            snackbarMessage: 'Failed to fetch blog posts.',
            render: 'error',
          });
        }
      });
  }

  checkForEmptyResponseAndSetState(data) {
    const newRender = data.length === 0 ? 'expandSearch' : 'posts';
    const morePagesAvailable = data.length === this.pageSize;

    this.setState({ posts: data, render: newRender, morePagesAvailable });
  }

  handleLoadMore() {
    const {
      currentPage,
      posts,
      activeAuthor,
      activeCategory,
      activeTag,
      activeSort,
    } = this.state;
    const nextPage = currentPage + 1;

    getBlogPosts({
      page: nextPage,
      pageSize: this.pageSize,
      author: activeAuthor.id,
      category: activeCategory,
      tag: activeTag,
      sort: activeSort,
    })
      .then((response) => {
        const allPosts = [...posts, ...response.data];
        const morePagesAvailable = response.data.length === this.pageSize;
        this.setState({ posts: allPosts, currentPage: nextPage, morePagesAvailable });
      })
      .catch(() => {
        this.setState({
          ...this.errorState,
          snackbarMessage: 'Failed to fetch blog posts.',
          render: 'error',
        });
      });
  }

  handleClose(e, reason) {
    if (reason === 'clickaway') {
      return;
    }
    this.setState({ snackbarOpen: false });
  }

  handleMenuClose() {
    this.setState({ anchorEl: null });
  }

  handleMenuOpen(e) {
    this.setState({ anchorEl: e.currentTarget });
  }

  handleToggleModal(value) {
    this.setState({ showSubscriptionModal: value });
  }

  render() {
    const { classes, email } = this.props;
    const {
      posts,
      render,
      postDisplay,
      snackbarOpen,
      snackbarMessage,
      snackbarVariant,
      authors,
      categories,
      tags,
      activeSort,
      activeCategory,
      activeAuthor,
      activeTag,
      morePagesAvailable,
      anchorEl,
      showSubscriptionModal,
      query,
    } = this.state;

    const isErrorOrLoadingState = render === 'loading' || render === 'error';
    const isErrorOrLoadingOrExpandSearch = isErrorOrLoadingState || render === 'expandSearch';
    const displayLoadMoreButton = morePagesAvailable && render === 'posts';

    const postDisplayRender = postDisplay === 'grid' ? <BlogPostGrid posts={posts} /> : <BlogPostList posts={posts} />;
    const blogPostsConditionalRender = {
      loading: <LoadingSpinner />,
      posts: postDisplayRender,
      grid: <BlogPostGrid posts={posts} />,
      error: <ErrorMessage />,
      expandSearch: <ExpandSearch />,
    };

    return (
      <div className={classes.page}>
        <Helmet>
          <title>Nia Blog - All Posts</title>
          <meta name="description" content="Experience the magic of Nia through stories and shared experiences provided by our Nia Community." />
        </Helmet>
        <NavBar />
        <main className={classes.container}>
          <div className={classes.appBarSpacer} />
          <img src={BlogHero} className={classes.hero} alt="Person sitting on end of dock looking at lake and mountains." />
          <Typography className={classes.header} variant="h4">Welcome to the Nia Blog</Typography>
          <Typography className={classes.subHeader}>
            {/* eslint-disable-next-line max-len */}
            Experience the magic of Nia through stories and shared experiences provided by our Nia Community.
          </Typography>
          {/* SEARCH BAR */}
          <div className={classes.searchWrapper}>
            <TextField
              size="small"
              onChange={this.handleSearchChange}
              onKeyDown={this.handleKeyPress}
              className={classes.textField}
              value={query}
              variant="outlined"
            />
            <Button
              onClick={this.handleSearch}
              variant="contained"
              color="secondary"
              className={classes.searchButton}
              size="large"
              disabled={!query || render === 'loading'}
            >
              Search
            </Button>
          </div>
          <div className={classes.offsetLeft}>
            {/* RENDER VIEW BUTTONS */}
            <ButtonGroup size="large" orientation="vertical" aria-label="vertical contained button group" variant="text" className={classes.renderButtons}>
              <Button disabled={isErrorOrLoadingOrExpandSearch || postDisplay === 'list'} onClick={() => this.handleViewChange('list')} endIcon={<ListIcon />}>List</Button>
              <Button disabled={isErrorOrLoadingOrExpandSearch || postDisplay === 'grid'} onClick={() => this.handleViewChange('grid')} endIcon={<GridIcon />}>Grid</Button>
            </ButtonGroup>
            <Button variant="outlined" fullwidth="false" onClick={() => this.handleToggleModal(true)} className={classes.subscribeButton}>Subscribe</Button>
          </div>
          {/* POST SORT CONTROLS */}
          <div className={classes.sortControlBox}>
            <SortControls
              className={classes.sortControl}
              isErrorOrLoadingState={isErrorOrLoadingState}
              render={render}
              authors={authors}
              categories={categories}
              tags={tags}
              activeSort={activeSort}
              activeAuthor={activeAuthor}
              activeCategory={activeCategory}
              activeTag={activeTag}
              handleClear={this.handleClear}
              handleDateSelect={this.handleDateSelect}
              handleTagSelect={this.handleTagSelect}
              handleAuthorSelect={this.handleAuthorSelect}
              handleCategorySelect={this.handleCategorySelect}
              handleToggleModal={this.handleToggleModal}
            />
          </div>
          <div className={classes.renderContainer}>
            {blogPostsConditionalRender[render]}
          </div>
          {displayLoadMoreButton
            && (
              <div className={classes.loadMoreButton}>
                <Button aria-label="load more" color="secondary" onClick={this.handleLoadMore}>
                  Load More
                </Button>
              </div>
            )
          }
          <NavFab className={classes.navFab} handleMenuOpen={this.handleMenuOpen} />

          {/* MOBILE MENU */}
          <Menu
            id="menu"
            anchorEl={anchorEl}
            open={!!(anchorEl)}
            onClose={this.handleMenuClose}
            className={classes.mobileNav}
          >
            <SortControls
              className={classes.navSortControl}
              render={render}
              isErrorOrLoadingState={isErrorOrLoadingState}
              authors={authors}
              categories={categories}
              tags={tags}
              activeSort={activeSort}
              activeAuthor={activeAuthor}
              activeCategory={activeCategory}
              activeTag={activeTag}
              handleClear={this.handleClear}
              handleDateSelect={this.handleDateSelect}
              handleTagSelect={this.handleTagSelect}
              handleAuthorSelect={this.handleAuthorSelect}
              handleCategorySelect={this.handleCategorySelect}
              handleToggleModal={this.handleToggleModal}
            />
          </Menu>
          {/* SNACKBAR */}
          <Snackbar
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'left',
            }}
            open={snackbarOpen}
            autoHideDuration={5000}
            onClose={this.handleClose}
          >
            <MySnackbarContentWrapper
              onClose={this.handleClose}
              variant={snackbarVariant}
              message={snackbarMessage}
            />
          </Snackbar>
          {/* SUBSCRIPTION MODAL */}
          <BlogSubscribeModal
            open={showSubscriptionModal}
            onClose={() => this.handleToggleModal(false)}
            email={email}
          />
          <FooterSpacer className={classes.footerSpacer} />
        </main>
        <Footer />
      </div>
    );
  }
}

Blog.defaultProps = {
  email: '',
};

Blog.propTypes = {
  classes: PropTypes.objectOf(PropTypes.string).isRequired,
  email: PropTypes.string,
};

function mapStateToProps(state) {
  const { user: { email = '' } = {} } = state.loginReducer;
  return {
    email,
  };
}

export default withStyles(styles)(connect(mapStateToProps)(Blog));

// LOADING SPINNER
let LoadingSpinner = ({ classes }) => (
  <div className={classes.center}>
    <CircularProgress color="secondary" />
  </div>
);

LoadingSpinner.propTypes = {
  classes: PropTypes.objectOf(PropTypes.string).isRequired,
};

LoadingSpinner = withStyles(styles)(LoadingSpinner);

// ERROR MESSAGE
let ErrorMessage = ({ classes }) => (
  <div>
    <Typography className={classes.errorMessage} variant="h5" color="textSecondary">Whoops! Sorry, we were unable to load blog posts at this time.</Typography>
  </div>
);

ErrorMessage.propTypes = {
  classes: PropTypes.objectOf(PropTypes.string).isRequired,
};

ErrorMessage = withStyles(styles)(ErrorMessage);

// EXPAND SEARCH
let ExpandSearch = ({ classes }) => (
  <div>
    <Typography className={classes.errorMessage} variant="h5" color="textSecondary">Uh Oh! We did not find any search that fit your criteria. Try expanding your search.</Typography>
  </div>
);

ExpandSearch.propTypes = {
  classes: PropTypes.objectOf(PropTypes.string).isRequired,
};

ExpandSearch = withStyles(styles)(ExpandSearch);

// SORT CONTROLS
let SortControls = ({
  className,
  classes,
  render,
  isErrorOrLoadingState,
  authors,
  categories,
  tags,
  handleClear,
  activeSort,
  activeAuthor,
  activeCategory,
  activeTag,
  handleDateSelect,
  handleTagSelect,
  handleAuthorSelect,
  handleCategorySelect,
  handleToggleModal,
}) => (
  <FormControl className={className}>
    <Typography color="textSecondary" className={classes.sortBy}>Sort By:</Typography>
    <TextField select disabled={isErrorOrLoadingState} fullWidth className={classes.select} label="Date" value={activeSort} onChange={handleDateSelect}>
      <MenuItem value="newest">Newest</MenuItem>
      <MenuItem value="oldest">Oldest</MenuItem>
    </TextField>
    <Typography color="textSecondary" className={classes.filterBy}>Filter by:</Typography>
    <TextField select disabled={authors.length === 0 || isErrorOrLoadingState} fullWidth label="Author" value={activeAuthor} onChange={handleAuthorSelect}>
      {authors.map(author => (
        <MenuItem key={author.id} value={author}>{author.name}</MenuItem>
      ))}
    </TextField>
    <TextField select disabled={categories.length === 0 || isErrorOrLoadingState} fullWidth className={classes.select} label="Category" value={activeCategory} onChange={handleCategorySelect}>
      {categories.map(category => (
        <MenuItem key={category.id} value={category.name}>{category.name}</MenuItem>
      ))}
    </TextField>
    <TextField select disabled={tags.length === 0 || isErrorOrLoadingState} fullWidth className={classes.select} label="Tag" value={activeTag} onChange={handleTagSelect}>
      {tags.map(tag => (
        <MenuItem key={tag.id} value={tag.name}>{tag.name}</MenuItem>
      ))}
    </TextField>
    <Button variant="outlined" fullwidth="false" onClick={handleClear} className={render === 'expandSearch' ? classes.clearButtonPulse : classes.clearButton}>clear</Button>
    <Button variant="outlined" fullwidth="false" onClick={handleToggleModal} className={classes.sortControlSubscribeButton}>Subscribe</Button>
  </FormControl>
);

SortControls.defaultProps = {
  authors: [],
  categories: [],
  tags: [],
  activeTag: '',
  activeSort: '',
  activeAuthor: {},
  activeCategory: '',
};

SortControls.propTypes = {
  className: PropTypes.string.isRequired,
  classes: PropTypes.objectOf(PropTypes.string).isRequired,
  render: PropTypes.string.isRequired,
  isErrorOrLoadingState: PropTypes.bool.isRequired,
  authors: PropTypes.arrayOf(authorShape),
  categories: PropTypes.arrayOf(categoryShape),
  tags: PropTypes.arrayOf(tagShape),
  activeTag: PropTypes.string,
  activeSort: PropTypes.string,
  activeAuthor: PropTypes.oneOfType([
    authorShape,
    PropTypes.string,
  ]),
  activeCategory: PropTypes.string,
  handleClear: PropTypes.func.isRequired,
  handleAuthorSelect: PropTypes.func.isRequired,
  handleDateSelect: PropTypes.func.isRequired,
  handleCategorySelect: PropTypes.func.isRequired,
  handleTagSelect: PropTypes.func.isRequired,
  handleToggleModal: PropTypes.func.isRequired,
};

SortControls = withStyles(styles)(SortControls);

// NAV FAB
let NavFab = ({ classes, className, handleMenuOpen }) => {
  const [activeFab, setActiveFab] = useState('Menu');
  const theme = useTheme();

  const handleScroll = () => {
    if (window.scrollY > 450) {
      if (activeFab !== 'To Top') setActiveFab('To Top');
    } else if (activeFab !== 'Menu') setActiveFab('Menu');
  };

  useEffect(() => {
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  });

  const handleClickToTopButton = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const transitionDuration = {
    enter: theme.transitions.duration.enteringScreen,
    exit: theme.transitions.duration.leavingScreen,
  };

  const fabs = [
    {
      icon: <UpArrow />,
      toolTip: 'To Top',
      onClick: handleClickToTopButton,
      class: classes.upArrow,
    },
    {
      icon: <Add />,
      toolTip: 'Menu',
      onClick: handleMenuOpen,
      class: classes.menuNav,
    },
  ];

  return (
    <div className={className}>
      {fabs.map(fab => (
        <Zoom
          key={fab.toolTip}
          in={fab.toolTip === activeFab}
          timeout={transitionDuration}
          style={{
            transitionDelay: `${fab.toolTip === activeFab ? transitionDuration.exit : 0}ms`,
          }}
          unmountOnExit
        >
          <Tooltip placement="top" title={fab.toolTip}>
            <Fab aria-label={fab.label} className={fab.class} color="primary" onClick={fab.onClick}>
              {fab.icon}
            </Fab>
          </Tooltip>
        </Zoom>
      ))}
    </div>
  );
};

NavFab.defaultProps = {
  className: '',
};

NavFab.propTypes = {
  classes: PropTypes.objectOf(PropTypes.string).isRequired,
  className: PropTypes.string,
  handleMenuOpen: PropTypes.func.isRequired,
};

NavFab = withStyles(styles)(NavFab);
